import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getStaffForTagging, TaggedUser } from '../../../../../api/staff';
import './TaggedUserDropdown.scss';

export type TaggedUserDropdownPosition = {
  top: number;
  left: number;
  placement?: 'above' | 'below';
  width?: number;
};

interface TaggedUserDropdownProps {
  isVisible: boolean;
  position: TaggedUserDropdownPosition;
  onSelectUser: (user: TaggedUser) => void;
  onClose: () => void;
  searchQuery: string;
  positionMode?: 'absolute' | 'fixed';
}

const TaggedUserDropdown: React.FC<TaggedUserDropdownProps> = ({
  isVisible,
  position,
  onSelectUser,
  onClose,
  searchQuery,
  positionMode = 'absolute'
}) => {
  const [users, setUsers] = useState<TaggedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible) {
      setLoading(true);
      // Debounce the search call
      const timeoutId = setTimeout(() => {
        fetchUsers(searchQuery);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [isVisible, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isVisible) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev =>
            prev < users.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev =>
            prev > 0 ? prev - 1 : users.length - 1
          );
          break;
        case 'Enter':
          event.preventDefault();
          if (users[selectedIndex]) {
            onSelectUser(users[selectedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, users, selectedIndex, onSelectUser, onClose]);

  const fetchUsers = async (query: string) => {
    try {
      setLoading(true);
      const staffUsers = await getStaffForTagging(query);
      setUsers(staffUsers);
      setSelectedIndex(0);
    } catch (error) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  const placement = position.placement ?? 'below';
  const dropdown = (
    <div
      ref={dropdownRef}
      className={`tagged-user-dropdown${placement === 'above' ? ' tagged-user-dropdown--above' : ''}`}
      style={{
        position: positionMode,
        top: position.top,
        left: position.left,
        width: position.width,
        zIndex: positionMode === 'fixed' ? 2101 : 1000,
        transform: placement === 'above' ? 'translateY(-100%)' : undefined
      }}
    >
      <div className="dropdown-header">
        <span>Tag a user</span>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>
      <div className="dropdown-content">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : users.length === 0 ? (
          <div className="no-results">No users found</div>
        ) : (
          users.map((user, index) => (
            <div
              key={user.id}
              className={`user-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => onSelectUser(user)}
            >
              <div className="user-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 8C10.2091 8 12 6.20914 12 4C12 1.79086 10.2091 0 8 0C5.79086 0 4 1.79086 4 4C4 6.20914 5.79086 8 8 8Z"
                    fill="#6B7280"
                  />
                  <path
                    d="M8 10C3.58172 10 0 13.5817 0 18H16C16 13.5817 12.4183 10 8 10Z"
                    fill="#6B7280"
                  />
                </svg>
              </div>
              <div className="user-info">
                <div className="user-name">{user.display_name}</div>
                <div className="user-role">{user.role}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  if (positionMode === 'fixed') {
    return createPortal(dropdown, document.body);
  }

  return dropdown;
};

export default TaggedUserDropdown;
